import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { OppositeDeathOrderSuggestionsCallout } from '@/modules/aiDP/components/OppositeDeathOrderSuggestionsCallout';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useDPFormSuggestionsContext } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { useShowAIDPSuggestionsMatcher } from '@/modules/aiDP/hooks/useShowAIDPSuggestionsMatcher';
import { AISuggestionsMatcher } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher';
import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useAiSuggestionsMatcherContext } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { useOrderedDyingClients } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { PrimaryClientDeathEvent } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import {
  AiSuggestionDispositiveProvisionDeathEvent,
  AiSuggestionKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { PulidKind } from '@/utils/pulid';

function getEffectiveDpDeathEvent(suggestion: {
  dpDeathEvent?: AiSuggestionDispositiveProvisionDeathEvent | null;
  dpRecipient?: { distributionDeathEvent?: string | null } | null;
}): AiSuggestionDispositiveProvisionDeathEvent | null {
  if (suggestion.dpDeathEvent) return suggestion.dpDeathEvent;

  // Fallback: map dpRecipient.distributionDeathEvent string to enum.
  // dpDeathEvent can be null when the backend hasn't populated it yet for V2 suggestions.
  const fallback = suggestion.dpRecipient?.distributionDeathEvent;
  if (fallback === 'firstDeath')
    return AiSuggestionDispositiveProvisionDeathEvent.UponFirstDeath;
  if (fallback === 'secondDeath')
    return AiSuggestionDispositiveProvisionDeathEvent.UponSecondDeath;

  return null;
}

interface DPFormRecipientSuggestionsMatcherProps {
  /** Callback when user clicks "Switch death order" in the callout */
  onSwitchDeathOrder?: () => void;
  /** When provided, renders a matcher scoped to a specific death-event section. */
  deathEvent?: PrimaryClientDeathEvent;
}

interface SuggestionCountByDeathEvent {
  firstDeath: number;
  secondDeath: number;
}

interface MatcherDeathEventSuggestion {
  kind: AiSuggestionKind;
  dpDeathEvent?: AiSuggestionDispositiveProvisionDeathEvent | null;
  dpRecipient?: { distributionDeathEvent?: string | null } | null;
}

export function filterSuggestionsForDeathEvent<
  T extends MatcherDeathEventSuggestion,
>({
  suggestions,
  deathEvent,
}: {
  suggestions: T[];
  deathEvent: PrimaryClientDeathEvent;
}): T[] {
  return suggestions.filter((suggestion) => {
    if (
      suggestion.kind !== AiSuggestionKind.DpRecipientV2 ||
      !('dpDeathEvent' in suggestion)
    ) {
      return true;
    }

    const effectiveDeathEvent = getEffectiveDpDeathEvent(suggestion);

    if (deathEvent === PrimaryClientDeathEvent.FIRST_DEATH) {
      return (
        effectiveDeathEvent ===
        AiSuggestionDispositiveProvisionDeathEvent.UponFirstDeath
      );
    }

    return (
      effectiveDeathEvent ===
      AiSuggestionDispositiveProvisionDeathEvent.UponSecondDeath
    );
  });
}

export function DPFormRecipientSuggestionsMatcher({
  onSwitchDeathOrder,
  deathEvent,
}: DPFormRecipientSuggestionsMatcherProps) {
  const showMatcher = useShowAIDPSuggestionsMatcher();

  const { isTwoClientHousehold } = useDispositiveProvisionsContext();

  const { targetKind } = useDispositiveProvisionSuggestionsContext();

  const {
    dpSuggestionKinds,
    hasUnacknowledgedKGV2RecipientSuggestions,
    unacknowledgedRecipientSuggestions,
    hasUnacknowledgedRecipientSuggestions,
    oppositeDeathOrderHasUnacknowledgedSuggestions,
    oppositeDeathOrderUnacknowledgedCount,
    oppositeDeathOrderClientName,
    isOppositeDeathOrderPrimary,
  } = useDPFormSuggestionsContext();

  const { setMatcherVariant } = useAiSuggestionsMatcherContext();

  // Get the ordered dying clients for the death event options
  const [firstDyingClient, secondDyingClient] = useOrderedDyingClients();

  // The selected death event to apply the suggestions to, once matched.
  // Default to FIRST_DEATH for Entity targets (the only case where death event selection is shown)
  const [selectedDeathEvent, setSelectedDeathEvent] =
    useState<PrimaryClientDeathEvent>(PrimaryClientDeathEvent.FIRST_DEATH);
  const [hasManuallySelectedDeathEvent, setHasManuallySelectedDeathEvent] =
    useState(false);
  const isSectionScopedMatcher = deathEvent !== undefined;

  const matcherDeathEvent = useMemo(() => {
    if (deathEvent) {
      return deathEvent;
    }

    if (targetKind === PulidKind.TestamentaryEntity) {
      return PrimaryClientDeathEvent.SECOND_DEATH;
    }

    if (
      targetKind === PulidKind.Entity &&
      !hasUnacknowledgedKGV2RecipientSuggestions
    ) {
      // Legacy/KGV1 DP suggestions only map to the first grantor death section.
      return PrimaryClientDeathEvent.FIRST_DEATH;
    }

    if (targetKind === PulidKind.Entity) {
      return selectedDeathEvent;
    }

    return undefined;
  }, [
    deathEvent,
    hasUnacknowledgedKGV2RecipientSuggestions,
    selectedDeathEvent,
    targetKind,
  ]);

  const matcherVariantKind = dpSuggestionKinds?.matcherVariantKind;
  const effectiveMatcherVariant = useMemo(() => {
    if (!matcherVariantKind) {
      return undefined;
    }

    const matcherVariant = getMatcherVariantForDeathEvent(
      matcherVariantKind,
      matcherDeathEvent
    );
    diagnostics.debug(
      `[DPFormRecipientSuggestionsMatcher][useEffect] setting matcher variant`,
      {
        deathEvent: matcherDeathEvent,
        matcherVariant,
        targetKind,
      }
    );
    return matcherVariant;
  }, [matcherVariantKind, matcherDeathEvent, targetKind]);

  const deathEventOptions = useMemo(() => {
    return [
      {
        display: firstDyingClient?.displayGivenName
          ? `After ${firstDyingClient.displayGivenName}'s death`
          : 'First death',
        value: PrimaryClientDeathEvent.FIRST_DEATH,
      },
      {
        display: secondDyingClient?.displayGivenName
          ? `After ${secondDyingClient.displayGivenName}'s death`
          : 'Second death',
        value: PrimaryClientDeathEvent.SECOND_DEATH,
      },
    ];
  }, [firstDyingClient, secondDyingClient]);

  const topRightContent = useMemo(() => {
    return (
      hasUnacknowledgedKGV2RecipientSuggestions &&
      !isSectionScopedMatcher &&
      targetKind === PulidKind.Entity &&
      isTwoClientHousehold && (
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="body-xs">Receive distributions:</Typography>
          <SelectInput
            sx={{ flex: 1 }}
            label="Apply suggestions to"
            hideLabel
            showEmptyValue={false}
            value={selectedDeathEvent}
            options={deathEventOptions}
            onChange={(event) => {
              const newDeathEvent = event.target
                .value as PrimaryClientDeathEvent;
              setHasManuallySelectedDeathEvent(true);
              setSelectedDeathEvent(newDeathEvent);

              if (matcherVariantKind) {
                // Map the current matcher variant to the appropriate death-event-specific variant
                const newMatcherVariant = getMatcherVariantForDeathEvent(
                  matcherVariantKind,
                  newDeathEvent
                );
                setMatcherVariant(newMatcherVariant);
              }
            }}
          />
        </Stack>
      )
    );
  }, [
    deathEventOptions,
    matcherVariantKind,
    hasUnacknowledgedKGV2RecipientSuggestions,
    isSectionScopedMatcher,
    isTwoClientHousehold,
    selectedDeathEvent,
    setMatcherVariant,
    targetKind,
  ]);

  const unacknowledgedSuggestionsForMatcher = useMemo(() => {
    if (!hasUnacknowledgedKGV2RecipientSuggestions || !matcherDeathEvent) {
      return unacknowledgedRecipientSuggestions;
    }

    return filterSuggestionsForDeathEvent({
      suggestions: unacknowledgedRecipientSuggestions,
      deathEvent: matcherDeathEvent,
    });
  }, [
    matcherDeathEvent,
    hasUnacknowledgedKGV2RecipientSuggestions,
    unacknowledgedRecipientSuggestions,
  ]);

  const unacknowledgedV2SuggestionCountByDeathEvent = useMemo(() => {
    return unacknowledgedRecipientSuggestions.reduce(
      (acc, suggestion) => {
        if (
          suggestion.kind !== AiSuggestionKind.DpRecipientV2 ||
          !('dpDeathEvent' in suggestion)
        ) {
          return acc;
        }

        const effectiveDeathEvent = getEffectiveDpDeathEvent(suggestion);

        if (
          effectiveDeathEvent ===
          AiSuggestionDispositiveProvisionDeathEvent.UponFirstDeath
        ) {
          acc.firstDeath += 1;
        }

        if (
          effectiveDeathEvent ===
          AiSuggestionDispositiveProvisionDeathEvent.UponSecondDeath
        ) {
          acc.secondDeath += 1;
        }

        return acc;
      },
      { firstDeath: 0, secondDeath: 0 }
    );
  }, [unacknowledgedRecipientSuggestions]);

  useEffect(() => {
    if (
      !hasUnacknowledgedKGV2RecipientSuggestions ||
      isSectionScopedMatcher ||
      targetKind !== PulidKind.Entity ||
      !isTwoClientHousehold
    ) {
      return;
    }

    const autoSelectedDeathEvent = getAutoSelectedDeathEvent({
      hasManuallySelectedDeathEvent,
      selectedDeathEvent,
      suggestionCountByDeathEvent: unacknowledgedV2SuggestionCountByDeathEvent,
    });

    // If the selected death event has no suggestions, but the opposite death
    // event does, switch to it to avoid showing an empty matcher by default.
    if (autoSelectedDeathEvent) {
      setSelectedDeathEvent(autoSelectedDeathEvent);
      if (dpSuggestionKinds?.matcherVariantKind) {
        setMatcherVariant(
          getMatcherVariantForDeathEvent(
            dpSuggestionKinds.matcherVariantKind,
            autoSelectedDeathEvent
          )
        );
      }
    }
  }, [
    dpSuggestionKinds?.matcherVariantKind,
    hasManuallySelectedDeathEvent,
    hasUnacknowledgedKGV2RecipientSuggestions,
    isSectionScopedMatcher,
    isTwoClientHousehold,
    selectedDeathEvent,
    setMatcherVariant,
    targetKind,
    unacknowledgedV2SuggestionCountByDeathEvent,
  ]);

  // Show the suggestions matcher if there are unacknowledged suggestions for the current death order
  if (
    showMatcher &&
    effectiveMatcherVariant &&
    (!isSectionScopedMatcher || unacknowledgedSuggestionsForMatcher.length > 0)
  ) {
    return (
      <Box px={2}>
        <AISuggestionsMatcher
          variant={effectiveMatcherVariant}
          suggestions={unacknowledgedSuggestionsForMatcher}
          topRightContent={topRightContent}
        />
      </Box>
    );
  }

  // If current death order has no suggestions but opposite death order does,
  // show the callout to prompt switching
  if (
    !isSectionScopedMatcher &&
    !hasUnacknowledgedRecipientSuggestions &&
    oppositeDeathOrderHasUnacknowledgedSuggestions &&
    onSwitchDeathOrder
  ) {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <OppositeDeathOrderSuggestionsCallout
          suggestionsCount={oppositeDeathOrderUnacknowledgedCount}
          clientName={oppositeDeathOrderClientName}
          isPrimaryDeathOrder={isOppositeDeathOrderPrimary}
          onSwitchDeathOrder={onSwitchDeathOrder}
        />
      </Box>
    );
  }

  return null;
}

/**
 * Maps the DP matcher variant to the appropriate firstDeath/secondDeath variant
 * for the DP Forms V2.
 *
 * @param matcherVariant
 * @param deathEvent
 */
function getMatcherVariantForDeathEvent(
  matcherVariant: AISuggestionsMatcherVariant,
  deathEvent?: PrimaryClientDeathEvent | null
) {
  if (!deathEvent) {
    return matcherVariant;
  }

  if (
    matcherVariant ===
    AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST
  ) {
    return deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
      ? AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH
      : AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH;
  }

  if (
    matcherVariant ===
    AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST
  ) {
    return deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
      ? AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH
      : AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH;
  }

  if (
    matcherVariant ===
    AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST
  ) {
    return deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
      ? AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH
      : AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH;
  }

  if (
    matcherVariant ===
    AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST
  ) {
    return deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
      ? AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH
      : AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH;
  }

  return matcherVariant;
}

export function getAutoSelectedDeathEvent({
  hasManuallySelectedDeathEvent = false,
  selectedDeathEvent,
  suggestionCountByDeathEvent,
}: {
  hasManuallySelectedDeathEvent?: boolean;
  selectedDeathEvent: PrimaryClientDeathEvent;
  suggestionCountByDeathEvent: SuggestionCountByDeathEvent;
}): PrimaryClientDeathEvent | null {
  if (hasManuallySelectedDeathEvent) {
    return null;
  }

  const selectedDeathEventCount =
    selectedDeathEvent === PrimaryClientDeathEvent.FIRST_DEATH
      ? suggestionCountByDeathEvent.firstDeath
      : suggestionCountByDeathEvent.secondDeath;

  const oppositeDeathEvent =
    selectedDeathEvent === PrimaryClientDeathEvent.FIRST_DEATH
      ? PrimaryClientDeathEvent.SECOND_DEATH
      : PrimaryClientDeathEvent.FIRST_DEATH;
  const oppositeDeathEventCount =
    oppositeDeathEvent === PrimaryClientDeathEvent.FIRST_DEATH
      ? suggestionCountByDeathEvent.firstDeath
      : suggestionCountByDeathEvent.secondDeath;

  if (selectedDeathEventCount === 0 && oppositeDeathEventCount > 0) {
    return oppositeDeathEvent;
  }

  return null;
}
